import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, ShoppingCart, Truck, Shield, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      if (response.data.size?.length > 0) {
        setSelectedSize(response.data.size[0]);
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    const success = await addToCart(product.id, selectedSize);
    if (success) {
      // Cart context handles the toast
    }
  };

  const handleAddToWishlist = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add to wishlist');
      return;
    }
    try {
      await axios.post(`${API}/wishlist/add`, { product_id: product.id });
      toast.success('Added to wishlist');
    } catch (error) {
      toast.error('Failed to add to wishlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-concrete-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-chicago-red border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-concrete-white flex flex-col items-center justify-center">
        <p className="font-bebas text-2xl text-cement-grey">PRODUCT NOT FOUND</p>
        <Link to="/products">
          <Button className="mt-4">Back to Shop</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-concrete-white" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/products" className="inline-flex items-center text-cement-grey hover:text-deep-obsidian transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="uppercase text-xs tracking-widest font-bold">Back to Shop</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-white border border-gray-200 overflow-hidden">
              <img
                src={product.images?.[selectedImage] || 'https://via.placeholder.com/800'}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-4">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 border-2 overflow-hidden transition-colors ${
                      selectedImage === index ? 'border-chicago-red' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img src={image} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-cement-grey text-sm uppercase tracking-wider">{product.brand}</span>
              <span className="w-1 h-1 bg-cement-grey rounded-full" />
              <span className="text-cement-grey text-sm uppercase tracking-wider">{product.era}</span>
            </div>
            
            <h1 className="font-anton text-4xl md:text-5xl tracking-tighter uppercase" data-testid="product-name">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 mt-4">
              <span className="font-bebas text-4xl text-deep-obsidian" data-testid="product-price">
                ${product.price.toFixed(2)}
              </span>
              {product.original_price && (
                <span className="text-cement-grey text-lg line-through">
                  ${product.original_price.toFixed(2)}
                </span>
              )}
            </div>

            {/* Condition Badge */}
            <div className="mt-6 inline-flex items-center gap-2 bg-deep-obsidian text-white px-4 py-2">
              <span className="font-bebas tracking-wider">{product.condition}</span>
              {product.condition === 'Deadstock' && <Check className="w-4 h-4 text-chicago-red" />}
            </div>

            {/* Size Selection */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <span className="font-bebas text-lg tracking-wider">SELECT SIZE (US)</span>
                <Link to="/size-guide" className="text-chicago-red text-sm underline hover:no-underline">
                  Size Guide
                </Link>
              </div>
              <div className="grid grid-cols-4 gap-2" data-testid="size-selector">
                {product.size.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`py-3 border-2 font-bold transition-all ${
                      selectedSize === size
                        ? 'border-deep-obsidian bg-deep-obsidian text-white'
                        : 'border-gray-200 hover:border-deep-obsidian'
                    }`}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Stock Info */}
            <p className="mt-4 text-cement-grey text-sm">
              {product.stock > 0 ? (
                <>Only <span className="text-chicago-red font-bold">{product.stock}</span> left in stock</>
              ) : (
                <span className="text-chicago-red">Out of stock</span>
              )}
            </p>

            {/* Actions */}
            <div className="mt-8 flex gap-4">
              <Button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="flex-1 bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-6 rounded-sm btn-primary"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
              <Button
                onClick={handleAddToWishlist}
                variant="outline"
                className="py-6 px-6 border-2 border-deep-obsidian rounded-sm hover:bg-deep-obsidian hover:text-white"
                data-testid="add-to-wishlist-btn"
              >
                <Heart className="w-5 h-5" />
              </Button>
            </div>

            {/* Features */}
            <div className="mt-8 pt-8 border-t border-gray-200 space-y-4">
              <div className="flex items-center gap-4">
                <Shield className="w-6 h-6 text-chicago-red" />
                <div>
                  <p className="font-bold text-sm">100% Authentic</p>
                  <p className="text-cement-grey text-sm">Verified by our expert team</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Truck className="w-6 h-6 text-chicago-red" />
                <div>
                  <p className="font-bold text-sm">Worldwide Shipping</p>
                  <p className="text-cement-grey text-sm">Insured delivery in 5-10 days</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h2 className="font-bebas text-xl tracking-wider mb-4">DESCRIPTION</h2>
              <p className="text-cement-grey leading-relaxed" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Details Table */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h2 className="font-bebas text-xl tracking-wider mb-4">DETAILS</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-cement-grey">Brand</p>
                  <p className="font-bold">{product.brand}</p>
                </div>
                <div>
                  <p className="text-cement-grey">Year</p>
                  <p className="font-bold">{product.year}</p>
                </div>
                <div>
                  <p className="text-cement-grey">Era</p>
                  <p className="font-bold">{product.era}</p>
                </div>
                <div>
                  <p className="text-cement-grey">Condition</p>
                  <p className="font-bold">{product.condition}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
